import { Button } from '@/components/ui/button';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import FormFieldComponent from '@/components/FormFieldComponent';
import { useNavigate } from 'react-router-dom';
import ToolBar from '@/components/ToolBar';
import AlertBox from '@/components/AlertBox';
import { useToast } from '@/components/ui/use-toast';
import { FormFieldSelect } from '@/components/FormFieldSelect';
import { Form } from '@/components/ui/form';
import { UserInviteRequestRoleId, useRoleGetAll, UserUpdateByIdRequestStatus } from '@brainvaulttech/dgr-react-sdk';
import { useContext, useEffect, useMemo, useState } from 'react';
import FormFieldPhone, { createPhoneSchema, getPhoneNumberValue } from '@/components/FormFieldPhone';
import { capitalizeEveryWord, capitalizeFirstLetter, displayFullName } from '@/lib/utils';
import { NavigationBlockerContext } from '@/context/NavigationBlockerContext';
import { displayError } from '@/lib/error';
import {
  ZIP_CODE_SCHEMA,
  NAME_VALIDATION,
  STATE_VALIDATION,
  ADDRESS_LINE_VALIDATION,
  CITY_VALIDATION,
  EMAIL_ADDRESS_VALIDATION,
  ZIP_CODE_VALIDATION,
} from '@/utils/constant';

import Loader from '@/components/Loader';
import FormFieldMultiSelect from '@/components/FormFieldMultiSelect';
import FormFieldCheckbox from '@/components/FormFieldCheckbox';
import type { Team } from '@/types/team.types';
import { useTeamGetAll } from '@/services/teamService';
import { useInviteUser } from '@/services/userService';
import { roleDefaultTeams } from '@pages/users/UserUpdate';
import { isAdminLikeRole } from '@/lib/roles';

export const UserInviteFormSchema = z
  .object({
    email: z.string({ message: 'Email address is required' }).trim().email({ message: 'Email Address is invalid' }),
    firstName: z.string({ message: 'First name is required' }),
    lastName: z.string({ message: 'Last name is required' }),
    roleId: z.nativeEnum(UserInviteRequestRoleId, { message: 'Role is required' }),
    status: z.nativeEnum(UserUpdateByIdRequestStatus).optional(),
    servers: z
      .object({
        secondaryEmail: z
          .string()
          .trim()
          .email({ message: 'Secondary email address is invalid' })
          .optional()
          .nullable(),
        phoneNumber: createPhoneSchema(true),
        addressLine1: z.string({ message: 'Address line 1 is required' }),
        addressLine2: z.string().optional().nullable(),

        city: z.string({ message: 'City is required' }),
        state: z.string({ message: 'State is required' }).length(2, { message: 'State is invalid' }),
        zipCode: ZIP_CODE_SCHEMA,
      })
      .optional(),
    teamIds: z.array(z.string()).max(3, { message: 'A user can belong to a maximum of three teams' }).nullish(),
    supervisors: z.record(z.string(), z.boolean().default(false)).nullish(),
  })
  .refine(
    (data) => {
      if (data.roleId === 'SERVER') {
        if (
          !data.servers?.phoneNumber ||
          !data.servers?.addressLine1 ||
          !data.servers?.city ||
          !data.servers?.state ||
          !data.servers?.zipCode
        ) {
          return false;
        }
      }
      return true;
    },
    {
      message: 'Server details are required',
      path: ['servers'],
    }
  );

const InviteCreate = () => {
  const [isAlertBoxOpen, setIsAlertBoxOpen] = useState(false);
  const [isCancelAlertOpen, setIsCancelAlertOpen] = useState(false);
  const [openSupervisorChangeModal, setOpenSupervisorChangeModal] = useState<Team | null>(null);
  const [defaultTeamOptions, setDefaultTeamOptions] = useState<string[]>([]);
  const navigate = useNavigate();
  const { toast } = useToast();

  const form = useForm<z.infer<typeof UserInviteFormSchema>>({
    resolver: zodResolver(UserInviteFormSchema),
    mode: 'onBlur',
  });

  const { control, getValues, formState, handleSubmit, reset, watch, setValue } = form;
  const { isValid, isDirty } = formState;

  const { data: roles } = useRoleGetAll();
  const { data: teams, isFetching: isFetchingTeams } = useTeamGetAll();
  const roleId = watch('roleId');

  const roleOptions = useMemo(() => {
    if (roles?.data) {
      return roles?.data
        .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
        .map((role) => ({ label: capitalizeFirstLetter(role.name), value: role.roleId }));
    }
    return [];
  }, [roles, roleId]);

  const teamIds = watch('teamIds');

  const { setIsDirty } = useContext(NavigationBlockerContext);

  useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty, setIsDirty]);

  useEffect(() => {
    if (roleId !== 'SERVER') {
      reset(
        {
          ...getValues(),
          servers: undefined,
        },
        { keepErrors: true }
      );
    }
  }, [roleId, reset, getValues]);

  const { mutate: inviteUser, status } = useInviteUser();
  const onSubmit = () => {
    const values = getValues();
    const data = {
      ...values,
      ...(values.roleId === 'SERVER' && {
        secondaryEmail: values.servers?.secondaryEmail,
        phone: getPhoneNumberValue(values.servers?.phoneNumber),
        primaryAddress: values.servers?.addressLine1,
        secondaryAddress: values.servers?.addressLine2,
        city: values.servers?.city,
        state: values.servers?.state,
        zipCode: values.servers?.zipCode,
      }),
      teams: values.teamIds?.map((teamId) => ({
        teamId: teamId,
        isSupervisor: values.supervisors?.[teamId] ?? false,
      })),
    };

    delete data.servers;
    delete data.teamIds;
    delete data.supervisors;
    inviteUser(
      { data },
      {
        onSuccess: () => {
          toast({
            description: `An invite to join CMS has been sent to ${values.email}`,
          });
          navigate('/users');
        },
        onError: (e) => {
          setIsAlertBoxOpen(false);
          displayError(e, 5000);
        },
      }
    );
  };

  const teamsOptions = useMemo(() => {
    return (
      teams?.teams
        .slice()
        .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
        .map((team) => ({
          label1: capitalizeEveryWord(team.name),
          value: team.id,
          team: team,
        })) ?? []
    );
  }, [teams]);

  return (
    <Form {...form}>
      <div className="flex flex-col w-full">
        <ToolBar title="Invite User" />
        <Loader isLoading={isFetchingTeams}>
          <div className="flex flex-col gap-6">
            <div>
              <div className="grid grid-cols-2 gap-6 text-black">
                <FormFieldComponent
                  control={control}
                  name="firstName"
                  label="First Name"
                  placeholder="Enter First Name"
                  required
                  autofocus
                  {...NAME_VALIDATION}
                />
                <FormFieldComponent
                  control={control}
                  name="lastName"
                  label="Last Name"
                  placeholder="Enter Last Name"
                  required
                  {...NAME_VALIDATION}
                />
                <FormFieldComponent
                  control={control}
                  name="email"
                  label="Email Address"
                  placeholder="Enter Email Address"
                  required
                  {...EMAIL_ADDRESS_VALIDATION}
                />
                <FormFieldSelect
                  name="roleId"
                  label="Role"
                  placeholder="Select Role"
                  options={roleOptions}
                  required
                  onChange={(e) => {
                    const newRole = e ?? '';
                    const allTeams = teamsOptions ?? [];
                    const previousTeamIds = getValues('teamIds') ?? [];
                    const oldRole = roleId;
                    const oldDefaults = oldRole ? (roleDefaultTeams[oldRole] ?? []) : [];
                    const oldDefaultTeamIds = allTeams
                      .filter((t) => oldDefaults.includes(t.team.key))
                      .map((t) => t.value);
                    const newDefaults = roleDefaultTeams[newRole] ?? [];
                    const newDefaultTeamIds = allTeams
                      .filter((t) => newDefaults.includes(t.team.key))
                      .map((t) => t.value);

                    const updatedTeams = previousTeamIds.filter((id) => !oldDefaultTeamIds.includes(id));
                    const updatedSupervisors = {};

                    newDefaultTeamIds.forEach((id) => {
                      if (!updatedTeams.includes(id)) updatedTeams.push(id);
                    });

                    setValue('teamIds', updatedTeams, { shouldDirty: true });
                    setValue('supervisors', updatedSupervisors, { shouldDirty: true });

                    setDefaultTeamOptions(newDefaultTeamIds);
                    if (newRole !== 'SERVER') {
                      setValue('servers', undefined);
                    }
                  }}
                />
                <FormFieldMultiSelect
                  key={roleId}
                  options={teamsOptions.filter((team) => {
                    if (team.team.key === 'ADMIN') return isAdminLikeRole(roleId);
                    if (team.team.key === 'PROCESS_SERVER') return roleId === 'SERVER';
                    return true;
                  })}
                  name="teamIds"
                  disabledOptions={defaultTeamOptions}
                  label="Team(s)"
                  placeholder="Select Team(s)"
                  labelToDisplay={{
                    dropdown: 'label1',
                    selectedOption: 'label1',
                  }}
                />
              </div>
              <div className="grid grid-cols-1">
                {teamIds?.map((teamId) => {
                  const team = teamsOptions.find((team) => team.value === teamId);
                  return (
                    <FormFieldCheckbox
                      key={teamId}
                      label={`Supervisor - ${team?.label1}`}
                      name={`supervisors.${teamId}`}
                      control={control}
                      onCheckChange={(value) => {
                        const team = teamsOptions.find((team) => team.value === teamId)?.team;
                        if (value && team?.supervisor) setOpenSupervisorChangeModal(team);
                      }}
                    />
                  );
                })}
              </div>
            </div>
            {roleId === 'SERVER' && (
              <div className="w-full flex flex-col gap-4">
                <div className="grid grid-cols-2 gap-4 w-[100%]">
                  <FormFieldComponent
                    control={control}
                    name="servers.secondaryEmail"
                    label="Secondary Email Address"
                    placeholder="Enter Secondary Email Address"
                    maxLength={128}
                  />
                  <FormFieldPhone name="servers.phoneNumber" label="Phone Number" defaultCountry="us" required />
                </div>
                <FormFieldComponent
                  control={control}
                  name="servers.addressLine1"
                  label="Address Line 1"
                  placeholder="Enter Address Line 1"
                  required
                  {...ADDRESS_LINE_VALIDATION}
                />
                <FormFieldComponent
                  control={control}
                  name="servers.addressLine2"
                  label="Address Line 2"
                  placeholder="Enter Apt, suite etc. (Optional)"
                  {...ADDRESS_LINE_VALIDATION}
                />
                <div className="grid grid-cols-3 gap-4 w-[100%]">
                  <FormFieldComponent
                    control={control}
                    name="servers.city"
                    label="City"
                    placeholder="City"
                    required
                    {...CITY_VALIDATION}
                  />
                  <FormFieldComponent
                    control={control}
                    name="servers.state"
                    label="State"
                    placeholder="State"
                    required
                    {...STATE_VALIDATION}
                  />
                  <FormFieldComponent
                    control={control}
                    name="servers.zipCode"
                    label="Zip Code"
                    placeholder="Zip Code"
                    required
                    {...ZIP_CODE_VALIDATION}
                  />
                </div>
              </div>
            )}
            <div className="flex gap-4 ">
              <AlertBox
                triggerButton={
                  <Button
                    type="button"
                    variant="outline"
                    className="text-primary w-[75px] h-[36px] py-2 px-4 rounded-md focus:outline-none border-primaryhover:bg-[#EDF5FF]"
                    onClick={() => setIsCancelAlertOpen(true)}
                  >
                    Cancel
                  </Button>
                }
                title="Are you sure you want to cancel?"
                description="All of your current changes will be lost."
                warning={true}
                cancelBtnLabel="No"
                confirmBtnLabel="Yes"
                cancelBtnClick={() => setIsCancelAlertOpen(false)}
                confirmBtnClick={() => navigate('/users')}
                open={isCancelAlertOpen}
              />
              <AlertBox
                triggerButton={
                  <Button
                    variant="outline"
                    className="text-[#fff] h-[36px] w-[103px] py-2 px-4 rounded-md bg-primary border-primary hover:bg-[#12375C] hover:text-white"
                    disabled={!isValid}
                    onClick={() => setIsAlertBoxOpen(true)}
                  >
                    Send Invite
                  </Button>
                }
                title={`Do you want to invite ${getValues('firstName')} ${getValues('lastName')}?`}
                description={`The invitation will be sent to ${getValues('email')} to join the CMS for the ${roleOptions.find((r) => r.value === getValues('roleId'))?.label} role.`}
                cancelBtnLabel="Cancel"
                confirmBtnLabel="Invite"
                open={isAlertBoxOpen}
                isLoading={status === 'pending'}
                cancelBtnClick={() => setIsAlertBoxOpen(false)}
                confirmBtnClick={handleSubmit(onSubmit)}
              />
              {openSupervisorChangeModal && (
                <AlertBox
                  title={`Do you want to assign ${getValues('firstName') ?? ''} ${getValues('lastName') ?? ''} as supervisor?`}
                  description={`${displayFullName(openSupervisorChangeModal?.supervisor)} is currently the supervisor of ${capitalizeEveryWord(openSupervisorChangeModal?.name)}. If you continue, ${getValues('firstName') ?? ''} ${getValues('lastName') ?? ''} will become the supervisor of ${capitalizeEveryWord(openSupervisorChangeModal?.name)}.`}
                  cancelBtnLabel="No"
                  confirmBtnLabel="Yes"
                  open={!!openSupervisorChangeModal}
                  isLoading={status === 'pending'}
                  cancelBtnClick={() => {
                    setValue(`supervisors.${openSupervisorChangeModal.id}`, false);
                    setOpenSupervisorChangeModal(null);
                  }}
                  confirmBtnClick={() => {
                    setValue(`supervisors.${openSupervisorChangeModal.id}`, true);
                    setOpenSupervisorChangeModal(null);
                  }}
                />
              )}
            </div>
          </div>
        </Loader>
      </div>
    </Form>
  );
};

export default InviteCreate;
